import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Building2, AlertCircle, MapPin, Users, Settings, 
  Image, LogOut, Menu, X, ChevronRight, Brain, UserCog, Target,
  ClipboardList, Database, UserPlus, BarChart3, Briefcase, FileText,
  Gauge, Calculator, Network, BookOpen, MessageSquare, Presentation, Calendar,
  Package, Globe, DollarSign, ShoppingCart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_valuebot/artifacts/etd4vs0l_Absolin_logo_Blue-HD.png";

const sidebarLinks = [
  { href: "/admin-portal", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/admin-portal/analytics", icon: BarChart3, label: "Analytics", adminOnly: true },
  { href: "/admin-portal/users", icon: UserCog, label: "User Management" },
  { href: "/admin-portal/leads", icon: UserPlus, label: "Leads" },
  { href: "/admin-portal/verticals", icon: Building2, label: "Verticals" },
  { href: "/admin-portal/problems", icon: AlertCircle, label: "Problems & Solutions" },
  { href: "/admin-portal/clients", icon: Briefcase, label: "Client Master" },
  { href: "/admin-portal/case-studies", icon: FileText, label: "Case Studies" },
  { href: "/admin-portal/resources", icon: BookOpen, label: "Resources" },
  { href: "/admin-portal/feedback", icon: MessageSquare, label: "Feedback Management" },
  { href: "/admin-portal/presentation-generator", icon: Presentation, label: "Presentation Generator" },
  { href: "/admin-portal/bookings", icon: Calendar, label: "Booking Submissions" },
  { href: "/admin-portal/product-master", icon: Package, label: "Product Master" },
  { href: "/admin-portal/assessments", icon: Gauge, label: "Maturity Assessments" },
  { href: "/admin-portal/codn", icon: Calculator, label: "CODN Calculator" },
  { href: "/admin-portal/system-mapper", icon: Network, label: "System Mapper" },
  { href: "/admin-portal/knowledge-base", icon: Brain, label: "AI Consultant KB" },
  { href: "/admin-portal/sales-knowledge-base", icon: Target, label: "AI Sales KB" },
  { href: "/admin-portal/datasets", icon: Database, label: "AI Datasets" },
  { href: "/admin-portal/audit-log", icon: ClipboardList, label: "Audit Log", adminOnly: true },
  { href: "/admin-portal/branches", icon: MapPin, label: "Branches" },
  { href: "/admin-portal/experts", icon: Users, label: "Technical Experts" },
  { href: "/collections", icon: DollarSign, label: "Collections", collectionsOnly: true },
  { href: "/pre-sales-hub", icon: ShoppingCart, label: "Pre-Sales Hub", presalesOnly: true },
  { href: "/admin-portal/settings", icon: Settings, label: "Settings", adminOnly: true },
  { href: "/admin-portal/seo", icon: Globe, label: "SEO Management", adminOnly: true },
];

function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState({ name: 'Admin', role: 'admin' });

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("auth_token");
      const userDataStr = localStorage.getItem("user_data");
      
      if (!token) {
        navigate("/login");
        return;
      }

      if (userDataStr) {
        try {
          const parsed = JSON.parse(userDataStr);
          const hasAccess = parsed.role === 'admin' || parsed.can_access_collections || parsed.can_access_presales;
          if (!hasAccess) {
            navigate("/");
            return;
          }
          setUserData(parsed);
        } catch (e) {
          navigate("/login");
        }
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-slate-900 z-50 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_URL}
                alt="ABSOLIN"
                className="h-8 w-auto brightness-0 invert"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.filter(link => {
  if (link.adminOnly) return userData.role === 'admin';
  if (link.collectionsOnly) return userData.can_access_collections || userData.role === 'admin';
  if (link.presalesOnly) return userData.can_access_presales || userData.role === 'admin';
  return true;
}).map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  data-testid={`sidebar-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon className="h-5 w-5" />
                  {link.label}
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center text-white text-sm font-medium">
                {(userData.name || userData.email || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-200 text-sm font-medium truncate">
                  {userData.name || userData.email}
                </p>
                <p className="text-slate-500 text-xs">
                  {userData.role === 'admin' ? 'Administrator' : userData.can_access_collections ? 'Collections' : userData.can_access_presales ? 'Pre-Sales' : 'User'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-red-500/20"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
          </div>
          <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            View Site →
          </Link>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

export { AdminLayout };
export default AdminLayout;
